import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

const selectors = {
  DIALOG: 'dialog-component',
  PANEL: '.size-guide__panel',
  TRIGGER: '[aria-controls]',
  ARIA_CONTROLS: 'aria-controls',
  TABLE_ROWS: '.size-guide__tabs table tr',
  UNIT_INPUTS: 'input[name="size-guide-unit"]',
};

const names = {
  UNIT: 'size-guide-unit',
  TABS: 'size-guide-tabs',
};

const classes = {
  TFC_MODAL_OPEN: 'tfc-show-modal',
};

const CM_PER_INCH = 2.54;

export class SizeGuide extends Component {
  /** @type {MutationObserver | undefined} */
  _trueFitObserver;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.variantUpdate, (event) => event.stopPropagation());
    this.addEventListener(ThemeEvents.variantSelected, (event) => event.stopPropagation());
    this.addEventListener('change', (/** @type {Event} */ event) => this.handleUnitChange(event));
    this.observeTrueFit();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this._trueFitObserver?.disconnect();
  }

  // Close all open drawers when TrueFit modal opens — they conflict visually
  observeTrueFit() {
    this._trueFitObserver = new MutationObserver(() => {
      if (!document.body.classList.contains(classes.TFC_MODAL_OPEN)) return;

      this.closeDrawers();
    });
    this._trueFitObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
  }

  // Close every dialog-component currently open on the page
  closeDrawers() {
    document.querySelectorAll(selectors.DIALOG).forEach((element) => {
      /** @type {import('./dialog').DialogComponent} */ (element).closeDialog?.();
    });
  }

  /** @param {Event} event */
  handleUnitChange(event) {
    const target = event.target;

    if (!(target instanceof HTMLInputElement) || target.name !== names.UNIT) return;

    this.updateUnit(target.value);
  }

  /** @param {string} unit */
  updateUnit(unit) {
    const units = Array.from(this.querySelectorAll(selectors.UNIT_INPUTS)).map(
      (element) => /** @type {HTMLInputElement} */ (element).value,
    );

    if (units.length < 2) return;

    // Derive the "from" unit — whichever unit label isn't the new target
    const from = units.find((otherUnit) => otherUnit !== unit);

    if (!from) return;

    // Convert a numeric value between cm and inches
    const convert = /** @param {number} value */ (value) =>
      unit.toLowerCase() === 'cm'
        ? Math.round(value * CM_PER_INCH)
        : parseFloat((value / CM_PER_INCH).toFixed(1));

    // Only update rows whose first cell (label) contains the old unit string
    this.querySelectorAll(selectors.TABLE_ROWS).forEach((rowElement) => {
      const row = /** @type {HTMLTableRowElement} */ (rowElement);
      const label = row.cells[0];

      if (!label?.innerHTML.includes(from)) return;

      label.innerHTML = label.innerHTML.split(from).join(unit);
      [...row.cells].slice(1).forEach((cell) => {
        const num = parseFloat(cell.textContent ?? '');

        if (!isNaN(num)) cell.textContent = String(convert(num));
      });
    });
  }
}

if (!customElements.get('size-guide')) {
  customElements.define('size-guide', SizeGuide);
}

class SizeGuideTabs extends Component {
  /** @type {HTMLElement | null} */
  _panel = null;
  /** @type {string[]} */
  _options = [];
  /** @type {Element | null} */
  _sizeGuide = null;
  /** @type {() => void} */
  _variantHandler = () => this.updateActiveCell();

  connectedCallback() {
    super.connectedCallback();

    const { panel, prevButton, nextButton } = this.refs;

    this._sizeGuide = this.closest('size-guide');
    this._panel = /** @type {HTMLElement | null} */ (panel);
    this._options = this._panel
      ? /** @type {string[]} */ (JSON.parse(this._panel.dataset.options ?? '[]'))
      : [];

    this.addEventListener('change', (/** @type {Event} */ event) => {
      const target = event.target;

      if (target instanceof HTMLInputElement && target.name === names.TABS) {
        requestAnimationFrame(() => this.updateNavigation());
      }
    });

    this._prevButton = /** @type {HTMLButtonElement} */ (prevButton);
    this._nextButton = /** @type {HTMLButtonElement} */ (nextButton);
    this._prevButton?.addEventListener('click', () => this.scrollPanel(-1));
    this._nextButton?.addEventListener('click', () => this.scrollPanel(1));

    this._resizeObserver = new ResizeObserver(() => this.updateNavigation());
    this._resizeObserver.observe(this);

    this._sizeGuide?.addEventListener(ThemeEvents.variantSelected, this._variantHandler);

    this.normalizeTableColspan();
    this.tagVariantCells();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this._resizeObserver?.disconnect();
    this._sizeGuide?.removeEventListener(ThemeEvents.variantSelected, this._variantHandler);
  }

  // Add colspan to last cell of rows shorter than the widest row
  normalizeTableColspan() {
    this.querySelectorAll('table').forEach((table) => {
      const rows = [...table.rows];
      const maxCols = Math.max(...rows.map((row) => row.cells.length));

      rows.forEach((row) => {
        const { cells } = row;
        const last = cells[cells.length - 1];

        if (cells.length < maxCols && last) last.colSpan = maxCols - cells.length + 1;
      });
    });
  }

  // Match table cells against size options, tag matched cells with data-variant and wire up click
  tagVariantCells() {
    if (!this._panel || !this._options.length) return;

    // Matches strings that contain only digits (e.g. "08" → 8, strips leading zeros)
    const normaliseOptions = (/** @type {string} */ value) =>
      /^\d+$/.test(value) ? String(Number(value)) : value;

    this._panel.querySelectorAll('table td, table th').forEach((cell) => {
      // Normalize whitespace to handle WYSIWYG-injected tags (strong, p, span…)
      const text = (cell.textContent ?? '').replace(/\s+/g, ' ').trim();
      const match = this._options.find(
        (option) => normaliseOptions(text) === normaliseOptions(option),
      );

      if (!match) return;

      const element = /** @type {HTMLElement} */ (cell);

      element.dataset.variant = match;
      element.addEventListener('click', () => this.selectVariant(match));
    });

    this.updateActiveCell();
  }

  /** @param {string} value */
  selectVariant(value) {
    const input = /** @type {HTMLInputElement | null} */ (
      this._sizeGuide?.querySelector(`input[value="${CSS.escape(value)}"]:not(:checked)`)
    );

    if (!input) return;

    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  updateActiveCell() {
    if (!this._panel || !this._options.length) return;

    // Find whichever size option input is currently checked in the variant picker
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this._sizeGuide?.querySelector(
        this._options.map((option) => `input[value="${CSS.escape(option)}"]:checked`).join(','),
      )
    );
    const selected = checkedInput?.value;

    /** @type {HTMLElement | null} */
    let activeCell = null;

    this._panel.querySelectorAll('table [data-variant]').forEach((cell) => {
      const element = /** @type {HTMLElement} */ (cell);

      if (selected && element.dataset.variant === selected) {
        element.dataset.active = '';
        activeCell = element;
      } else {
        delete element.dataset.active;
      }
    });

    if (activeCell)
      /** @type {HTMLElement} */ (activeCell).scrollIntoView({
        block: 'nearest',
        inline: 'start',
        behavior: 'smooth',
      });
  }

  get visiblePanel() {
    return /** @type {HTMLElement | undefined} */ (
      Array.from(this.querySelectorAll(selectors.PANEL)).find(
        (element) => getComputedStyle(element).display !== 'none',
      )
    );
  }

  /** @param {1 | -1} direction */
  scrollPanel(direction) {
    const panel = this.visiblePanel;

    if (!panel) return;

    const pageWidth = panel.clientWidth / 2;
    const currentPage = Math.round(panel.scrollLeft / pageWidth);

    panel.scrollTo({ left: (currentPage + direction) * pageWidth, behavior: 'smooth' });
  }

  /** @param {HTMLElement} panel */
  updateButtonStates(panel) {
    if (!this._prevButton || !this._nextButton) return;

    this._prevButton.disabled = panel.scrollLeft <= 0;
    this._nextButton.disabled =
      Math.ceil(panel.scrollLeft) + panel.clientWidth >= panel.scrollWidth;
  }

  updateNavigation() {
    const navigation = /** @type {HTMLElement} */ (this.refs.navigation);

    if (!navigation) return;

    const panel = this.visiblePanel;
    const table = panel?.querySelector('table');
    // Show nav arrows only when the table overflows the component width
    const show = !!(table && table.scrollWidth > this.clientWidth);
    const display = show ? '' : 'none';

    if (navigation.style.display !== display) navigation.style.display = display;
    if (!show || !panel) return;

    // Bind scrollend once per panel to keep button states in sync
    if (!panel.dataset.scrollBound) {
      panel.dataset.scrollBound = '1';
      panel.addEventListener('scrollend', () => this.updateButtonStates(panel));
    }

    this.updateButtonStates(panel);
  }
}

if (!customElements.get('size-guide-tabs')) {
  customElements.define('size-guide-tabs', SizeGuideTabs);
}

class SizeGuideTrigger extends Component {
  open() {
    const id = this.querySelector(selectors.TRIGGER)?.getAttribute(selectors.ARIA_CONTROLS);
    const dialog = id ? document.getElementById(id) : null;

    if (!(dialog instanceof HTMLElement)) return;

    /** @type {import('./dialog').DialogComponent} */
    (dialog).showDialog?.();
  }
}

if (!customElements.get('size-guide-trigger')) {
  customElements.define('size-guide-trigger', SizeGuideTrigger);
}
